import { CR_API_ENDPOINTS } from '../constants';
import { apiService } from './api.service';
import { store } from '../store';

interface CRUser {
  id: string;
  email: string;
  name: string;
  avatar_url: string;
}

export interface ConstructionReview {
  id: string;
  task_id: string;
  task_name: string;
  generated_cr: Record<string, any>;
  updated_cr: Record<string, any>;
  changes: Record<string, any>;
  structured_data: Record<string, any> | null;
  aurora_full_data: Record<string, any> | null;
  created_by: CRUser | null;
  updated_by: CRUser | null;
  created_at: string;
  updated_at: string;
}

export interface PaginatedCRResponse {
  data: ConstructionReview[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CRListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'created_at' | 'updated_at' | 'task_name';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateCRDto {
  task_id: string;
  task_name: string;
  generated_cr: Record<string, any>;
  updated_cr: Record<string, any>;
  changes: Record<string, any>;
}

export interface UpdateCRDto {
  updated_cr?: Record<string, any>;
  changes?: Record<string, any>;
  structured_data?: Record<string, any>;
  aurora_full_data?: Record<string, any>;
}

class ConstructionReviewsService {
  async create(data: CreateCRDto): Promise<ConstructionReview> {
    try {
      const currentState = store.getState();
      const auroraData = currentState.createCR.aurora_data;
      const workflow = currentState.createCR.workflow;

      const { task, ...restFormData } = currentState.createCR.form_data;
      const structuredData = {
        ...restFormData,
        task: {
          customer_info: task.customer_info,
          aurora_design_id: task.aurora_design_id,
        },
        // Save workflow state so user can resume from the same step
        workflow: {
          current_step: workflow.current_step,
          completed_steps: workflow.completed_steps,
        },
      };

      const requestPayload: Record<string, unknown> = {
        ...data,
        structured_data: structuredData,
      };

      if (auroraData?.design_summary || auroraData?.structures_roofs_details) {
        requestPayload.aurora_full_data = auroraData;
      }

      return await apiService.post<ConstructionReview>(CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS, requestPayload);
    } catch (error) {
      throw new Error(`Failed to create construction review: ${error}`);
    }
  }

  async getAll(params?: CRListParams): Promise<PaginatedCRResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.set('page', String(params.page));
      if (params?.limit) queryParams.set('limit', String(params.limit));
      if (params?.search) queryParams.set('search', params.search);
      if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

      const qs = queryParams.toString();
      const url = qs
        ? `${CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS}?${qs}`
        : CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS;

      return await apiService.get<PaginatedCRResponse>(url);
    } catch (error) {
      throw new Error(`Failed to fetch construction reviews: ${error}`);
    }
  }

  async getById(id: string): Promise<ConstructionReview> {
    try {
      return await apiService.get<ConstructionReview>(`${CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS}/${id}`);
    } catch (error) {
      throw new Error(`Failed to fetch construction review: ${error}`);
    }
  }

  async getByTaskId(taskId: string): Promise<ConstructionReview[]> {
    try {
      return await apiService.get<ConstructionReview[]>(`${CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS}/by-task/${taskId}`);
    } catch (error) {
      throw new Error(`Failed to fetch construction reviews for task: ${error}`);
    }
  }

  async getLatestByTaskId(taskId: string): Promise<ConstructionReview | null> {
    try {
      return await apiService.get<ConstructionReview>(`${CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS}/latest-by-task/${taskId}`);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw new Error(`Failed to fetch latest construction review: ${error}`);
    }
  }

  async update(id: string, data: UpdateCRDto): Promise<ConstructionReview> {
    try {
      return await apiService.put<ConstructionReview>(`${CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS}/${id}`, data);
    } catch (error) {
      throw new Error(`Failed to update construction review: ${error}`);
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiService.delete<void>(`${CR_API_ENDPOINTS.CONSTRUCTION_REVIEWS}/${id}`);
    } catch (error) {
      throw new Error(`Failed to delete construction review: ${error}`);
    }
  }
}

export const constructionReviewsService = new ConstructionReviewsService();
